import { Component, OnInit, ViewChild } from '@angular/core';
import { DatePipe } from '@angular/common';
import { RouterLink } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PlayerFavouriteViewModel } from '../../../shared/models/PlayerFavouriteModels';
import { ScoutingFlowService } from '../../../shared/services/scouting-flow.service';
import { PositionPipe } from '../../../shared/pipes/position.pipe';
import { FlowItemViewModel } from '../../../shared/models/FlowItemModel';
import { GamePlayerInfoViewModel, GameStatisticsPlayerViewModel } from '../../../shared/models/GameModels';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-favorite-players',
  imports: [
    RouterLink,    
    PositionPipe,
    MatButtonModule,
    MatToolbarModule,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslatePipe
  ],
  templateUrl: './favorite-players.component.html',
  styleUrl: './favorite-players.component.css'
})
export class FavoritePlayersComponent implements OnInit {
  private readonly emptyGuid = '00000000-0000-0000-0000-000000000000';
  players: PlayerFavouriteViewModel[] = [];
  isLoading = false;
  selectedNoOfDaysBack = 7;
  readonly periodOptions = [
    { value: 1, label: 'Last day' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
     { value: 120, label: 'Last 120 days' }
  ];

  constructor(private scoutingFlowService: ScoutingFlowService) {}

  ngOnInit(): void {
    this.loadFavorites();
  }

  onPeriodChanged(noOfDaysBack: string): void {
    const parsedValue = Number(noOfDaysBack);
    if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
      return;
    }

    this.selectedNoOfDaysBack = parsedValue;
    this.loadFavorites();
  }

  getDivisionName(item: PlayerFavouriteViewModel): string {
    return item.DivisionName ?? item.Player?.Division ?? '';
  }

  getLatestReports(item: PlayerFavouriteViewModel): FlowItemViewModel[] {
    return (item.LatestReports ?? [])
      .filter((flowItem) => this.isReport(flowItem))
      .sort((a, b) => this.getDateTicks(b.DateCreated ?? b.SortDate) - this.getDateTicks(a.DateCreated ?? a.SortDate));
  }

  getLatestNotes(item: PlayerFavouriteViewModel): FlowItemViewModel[] {
    return (item.LatestReports ?? [])
      .filter((flowItem) => this.isNote(flowItem))
      .sort((a, b) => this.getDateTicks(b.DateCreated ?? b.SortDate) - this.getDateTicks(a.DateCreated ?? a.SortDate));
  }

  getLatestGames(item: PlayerFavouriteViewModel): GamePlayerInfoViewModel[] {
    return (item.LatestGames ?? [])
      .slice()
      .sort((a, b) => this.getDateTicks(b.GameDate) - this.getDateTicks(a.GameDate));
  }

  getLatestStatistics(item: PlayerFavouriteViewModel): GameStatisticsPlayerViewModel[] {
    return this.getLatestGames(item)
      .flatMap((game) => game.GameStatistics ?? [])
      .sort((a, b) => this.getDateTicks(b.GameDate) - this.getDateTicks(a.GameDate));
  }

  getTotalEventsCount(item: PlayerFavouriteViewModel): number {
    const gamesCount = this.getLatestGames(item).length;
    const reportsCount = this.getLatestReports(item).length;
    const notesCount = this.getLatestNotes(item).length;
    const statisticsCount = this.getLatestStatistics(item).length;
    return gamesCount + reportsCount + notesCount + statisticsCount;
  }

  getReportLabel(report: FlowItemViewModel): string {
    return report.TotalQualityText ? `Report (${report.TotalQualityText})` : 'Report';
  }

  getGameLabel(game: GamePlayerInfoViewModel): string {
    const homeName = game.HomeTeamName ?? '';
    const awayName = game.AwayTeamName ?? '';
    return `${homeName} vs ${awayName}`.trim();
  }

  getStatisticLabel(stat: GameStatisticsPlayerViewModel): string {
    const title = stat.Name || stat.Abbreviation || 'Statistic';
    const value = stat.FormattedValue || this.getStatisticValue(stat);
    return value ? `${title}: ${value}` : title;
  }

  private loadFavorites(): void {
    this.isLoading = true;

    this.scoutingFlowService.getFavouritesFlow(this.selectedNoOfDaysBack).subscribe({
      next: (result) => {
        this.players = result ?? [];
        this.isLoading = false;
      },
      error: () => {
        this.players = [];
        this.isLoading = false;
      }
    });
  }

  private isReport(flowItem: FlowItemViewModel): boolean {
    const flowItemType = (flowItem.FlowItemType ?? '').toLowerCase();
    if (flowItemType.includes('note')) {
      return false;
    }

    return !!flowItem.ReportId;
  }

  private isNote(flowItem: FlowItemViewModel): boolean {
    const flowItemType = (flowItem.FlowItemType ?? '').toLowerCase();
    if (flowItemType.includes('note')) {
      return true;
    }

    return !!flowItem.PlayerNoteId;
  }

  private getStatisticValue(stat: GameStatisticsPlayerViewModel): string {
    if (stat.ValueInteger !== undefined && stat.ValueInteger !== null) {
      return `${stat.ValueInteger}`;
    }

    if (stat.ValueDecimal !== undefined && stat.ValueDecimal !== null) {
      return `${stat.ValueDecimal}`;
    }

    if (stat.ValueSeconds !== undefined && stat.ValueSeconds !== null) {
      return `${stat.ValueSeconds}`;
    }

    if (stat.ValueMinutes !== undefined && stat.ValueMinutes !== null) {
      return `${stat.ValueMinutes}`;
    }

    return '';
  }

  private getDateTicks(date: Date | null | undefined): number {
    if (!date) {
      return 0;
    }

    return new Date(date).getTime();
  }

  getLastName(item: PlayerFavouriteViewModel): string {
    return item.PlayerView?.LastName ?? item.Player?.LastName ?? '';
  }

  getFirstName(item: PlayerFavouriteViewModel): string {
    return item.PlayerView?.FirstName ?? item.Player?.FirstName ?? '';
  }

  getPositionId(item: PlayerFavouriteViewModel): number {
    return item.PlayerView?.PositionId ?? item.Player?.PositionId ?? 0;
  }

  getDateOfBirth(item: PlayerFavouriteViewModel): Date | null {
    return item.PlayerView?.DateOfBirth ?? item.Player?.DateOfBirth ?? null;
  }

  getPlayerId(item: PlayerFavouriteViewModel): string {
    return this.getFirstValidId([
      item.Player?.LifelineScoutingId,
      item.PlayerView?.LifelineScoutingId,
      item.PlayerId,
      item.PlayerView?.PlayerId
    ]);
  }

  private getFirstValidId(ids: Array<string | undefined>): string {
    for (const id of ids) {
      if (id && id.trim() !== '' && id.toLowerCase() !== this.emptyGuid) {
        return id;
      }
    }

    return '';
  }
}
